/** Maximum width for resized images in pixels (default for cache) */
const MAX_WIDTH = 300;

/** Maximum width for project thumbnail uploads in pixels */
export const MAX_THUMBNAIL_UPLOAD_WIDTH = 600;

/**
 * Compress an image Blob using OffscreenCanvas and WebP format.
 * Maintains aspect ratio and applies quality settings.
 */
export async function compressImage(
  originalBlob: Blob,
  maxWidth = MAX_WIDTH
): Promise<Blob> {
  // If the image is SVG, skip compression and return as-is
  if (originalBlob.type === "image/svg+xml") {
    return originalBlob;
  }

  // Create an ImageBitmap from the original Blob
  const bitmap = await createImageBitmap(originalBlob);

  // If original width is already smaller than maxWidth, return original blob
  if (bitmap.width <= maxWidth) {
    return originalBlob;
  }

  // Calculate new height to maintain aspect ratio
  const canvas = new OffscreenCanvas(
    maxWidth,
    (bitmap.height * maxWidth) / bitmap.width
  );
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("[ImageUtils] Failed to get 2D context");
  }

  // Draw the image onto the canvas
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

  // Convert canvas to compressed WebP Blob
  return canvas.convertToBlob({
    type: "image/webp",
    quality: 0.8,
  });
}

/** Changes the file extension of a filename */
export function changeFileExtension(filename: string, newExtension: string): string {
  const nameWithoutExtension = filename.replace(/\.[^.]+$/, "");
  
  return `${nameWithoutExtension}.${newExtension}`;
}

/** Compresses an image file and returns a new File with WebP format */
export async function compressImageFile(
  file: File,
  maxWidth: number
): Promise<File> {
  const compressedBlob = await compressImage(file, maxWidth);
  const newFileName = changeFileExtension(file.name, "webp");
  
  return new File([compressedBlob], newFileName, {
    type: compressedBlob.type,
  });
}
